import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const empty = { title: "", slug: "", excerpt: "", content: "", cover_image: "", author: "Cake World USA", tags_str: "", published: true };

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [show, setShow] = useState(false);

  const load = () => api.get("/admin/blog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, tags_str: (p.tags || []).join(", ") });
    setShow(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      title: form.title, slug: form.slug, excerpt: form.excerpt, content: form.content,
      cover_image: form.cover_image, author: form.author,
      tags: form.tags_str.split(",").map((t) => t.trim()).filter(Boolean),
      published: !!form.published,
    };
    try {
      if (editing) await api.put(`/admin/blog/${editing.id}`, body);
      else await api.post(`/admin/blog`, body);
      toast.success(editing ? "Post updated" : "Post created");
      setShow(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/blog/${p.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Journal</div>
          <h1 className="font-display text-4xl text-brand-ink mt-2">Blog posts</h1>
        </div>
        <button onClick={openNew} data-testid="new-post-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-ink text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      <div className="mt-8 grid gap-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`post-row-${p.slug}`} className="bg-white border border-brand-line rounded-2xl p-4 flex items-center gap-4">
            <img src={p.cover_image} alt="" className="w-20 h-14 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-brand-ink">{p.title}</div>
              <div className="text-xs text-brand-stone">/{p.slug} · {new Date(p.created_at).toLocaleDateString()}</div>
            </div>
            {!p.published && <span className="text-[10px] uppercase tracking-widest text-brand-stone font-bold">Draft</span>}
            <button onClick={() => openEdit(p)} data-testid={`edit-post-${p.slug}`} className="w-9 h-9 grid place-items-center rounded-full border border-brand-line"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p)} data-testid={`delete-post-${p.slug}`} className="w-9 h-9 grid place-items-center rounded-full border border-brand-line hover:bg-red-50 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-brand-ink/40 backdrop-blur-sm grid place-items-center p-4">
          <div className="bg-white rounded-3xl border border-brand-line max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-warm-lg">
            <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-brand-line">
              <h2 className="font-display text-2xl text-brand-ink">{editing ? "Edit post" : "New post"}</h2>
              <button onClick={() => setShow(false)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-brand-pink/40"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4" data-testid="post-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Title" required value={form.title} onChange={set("title")} />
                <F label="Slug" required value={form.slug} onChange={set("slug")} />
                <F label="Author" value={form.author} onChange={set("author")} />
              </div>
              <ImageUploader
                label="Cover image"
                value={form.cover_image}
                onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))}
                testIdPrefix="post-cover"
              />
              <F label="Excerpt" required value={form.excerpt} onChange={set("excerpt")} />
              <T label="Content" required value={form.content} onChange={set("content")} rows={10} />
              <F label="Tags (comma separated)" value={form.tags_str} onChange={set("tags_str")} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.published} onChange={set("published")} /> Published</label>
              <div className="flex gap-3 pt-2">
                <button type="submit" data-testid="save-post" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-ink text-white text-sm font-semibold">Save</button>
                <button type="button" onClick={() => setShow(false)} className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-brand-line text-brand-ink text-sm font-semibold">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, required, value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-brand-ink">{label} {required && <span className="text-brand-red">*</span>}</label>
      <input type="text" value={value || ""} required={required} onChange={onChange}
        className="mt-1.5 w-full h-11 rounded-xl bg-white border border-brand-line px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red" />
    </div>
  );
}
function T({ label, required, value, onChange, rows = 4 }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-brand-ink">{label} {required && <span className="text-brand-red">*</span>}</label>
      <textarea rows={rows} value={value || ""} required={required} onChange={onChange}
        className="mt-1.5 w-full rounded-xl bg-white border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red resize-none" />
    </div>
  );
}
